import React, { useState } from 'react';
import { BellOff, ChevronDown, ExternalLink, ShieldCheck } from 'lucide-react';
import { Button, Card, EmptyState, Pill, Skeleton } from '../../../components/ui';
import type { AlertStatus, AlertView } from '../../../types/monitoring';
import { ALERT_KIND_LABEL, formatDate, hostOf, metricLabel, timeAgo, windowLabel } from '../format';
import { MetaLine, SentimentMark, SeverityPill, SubjectLink } from './ui';

const STATUS_LABEL: Record<AlertStatus, string> = {
  open: 'Open',
  acknowledged: 'Acknowledged',
  resolved: 'Resolved',
};

/** Numbers rounded for reading, not for maths */
const formatMetric = (value: number): string =>
  Number.isInteger(value) ? String(value) : Math.abs(value) < 10 ? value.toFixed(2) : value.toFixed(1);

interface AlertRowProps {
  alert: AlertView;
  busy: boolean;
  onStatus: (id: string, status: AlertStatus) => void;
}

const AlertRow: React.FC<AlertRowProps> = ({ alert, busy, onStatus }) => {
  const [open, setOpen] = useState(false);
  const evidence = alert.signals || [];
  const metrics = Object.entries(alert.evidence?.metrics || {});
  const related = alert.evidence?.related || [];
  const evidenceCount = evidence.length || alert.evidence?.signalIds?.length || 0;
  const hasEvidence = evidenceCount > 0 || metrics.length > 0 || related.length > 0;
  const window = windowLabel(alert.windowStart, alert.windowEnd);

  return (
    <div className={`px-4 py-3.5 ${alert.status === 'resolved' ? 'opacity-60' : ''}`}>
      <div className="flex flex-wrap items-center gap-2">
        <SeverityPill severity={alert.severity} />
        <Pill tone="outline">{ALERT_KIND_LABEL[alert.kind]}</Pill>
        {alert.status !== 'open' && <Pill tone="neutral">{STATUS_LABEL[alert.status]}</Pill>}
        <span className="ml-auto text-[12px] text-neutral-400" title={new Date(alert.createdAt).toLocaleString()}>
          {timeAgo(alert.createdAt)}
        </span>
      </div>

      <p className="mt-2 text-[14px] leading-snug text-ink">{alert.title}</p>
      <p className="mt-1 text-[13px] leading-relaxed text-neutral-500">{alert.explanation}</p>

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
        {alert.subject && <SubjectLink subject={alert.subject} />}
        <MetaLine parts={[window]} />
      </div>

      {hasEvidence && (
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          className="mt-2.5 inline-flex h-7 items-center gap-1 rounded-full text-[12px] text-neutral-500 transition-colors hover:text-ink"
        >
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} strokeWidth={1.75} />
          {open ? 'Hide evidence' : `Evidence · ${evidenceCount} ${evidenceCount === 1 ? 'signal' : 'signals'}`}
        </button>
      )}

      {open && (
        <div className="mt-2 space-y-3 rounded-xl bg-neutral-50 p-3">
          {metrics.length > 0 && (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-4">
              {metrics.map(([key, value]) => (
                <div key={key} className="min-w-0">
                  <dt className="truncate text-[11.5px] text-neutral-400">{metricLabel(key)}</dt>
                  <dd className="text-[13px] tabular-nums text-ink">{formatMetric(value)}</dd>
                </div>
              ))}
            </dl>
          )}

          {evidence.length > 0 ? (
            <ul className="space-y-2">
              {evidence.map((signal) => (
                <li key={signal.id} className="min-w-0">
                  {signal.url ? (
                    <a
                      href={signal.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-start gap-1 text-[13px] leading-snug text-ink hover:underline hover:decoration-neutral-300 hover:underline-offset-2"
                    >
                      <span className="min-w-0">{signal.title}</span>
                      <ExternalLink className="mt-0.5 h-3 w-3 flex-shrink-0 text-neutral-400" strokeWidth={1.75} />
                    </a>
                  ) : (
                    <span className="text-[13px] leading-snug text-ink">{signal.title}</span>
                  )}
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2">
                    <MetaLine
                      parts={[signal.source || hostOf(signal.url), formatDate(signal.publishedAt || signal.detectedAt)]}
                    />
                    <SentimentMark value={signal.sentiment} />
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            evidenceCount > 0 && (
              <p className="text-[12.5px] text-neutral-500">
                Raised from {evidenceCount} {evidenceCount === 1 ? 'signal' : 'signals'} — open the entity's feed to read
                them.
              </p>
            )
          )}

          {related.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="text-[11.5px] text-neutral-400">Also mentioned</span>
              {related.map((subject) => (
                <SubjectLink key={subject.id} subject={subject} withAvatar={false} className="text-[12.5px]" />
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {alert.status === 'open' && (
          <Button size="xs" variant="secondary" disabled={busy} onClick={() => onStatus(alert.id, 'acknowledged')}>
            Acknowledge
          </Button>
        )}
        {alert.status !== 'resolved' ? (
          <Button size="xs" variant="secondary" disabled={busy} onClick={() => onStatus(alert.id, 'resolved')}>
            <ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.75} />
            Resolve
          </Button>
        ) : (
          <Button size="xs" variant="ghost" disabled={busy} onClick={() => onStatus(alert.id, 'open')}>
            Reopen
          </Button>
        )}
      </div>
    </div>
  );
};

export interface AlertsPanelProps {
  alerts: AlertView[];
  loading: boolean;
  busyId: string | null;
  onStatus: (id: string, status: AlertStatus) => void;
  /** Rendered instead of the empty state when something is wrong */
  notice?: React.ReactNode;
  emptyHint?: string;
}

export const AlertsPanel: React.FC<AlertsPanelProps> = ({
  alerts,
  loading,
  busyId,
  onStatus,
  notice,
  emptyHint = 'Nothing needs your attention',
}) => {
  if (loading) {
    return (
      <Card divided aria-busy="true" aria-label="Loading alerts">
        {Array.from({ length: 2 }).map((_, index) => (
          <div key={index} className="space-y-2 px-4 py-4">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-full" />
          </div>
        ))}
      </Card>
    );
  }

  if (notice) return <Card className="p-3">{notice}</Card>;

  if (alerts.length === 0) {
    return (
      <Card>
        <EmptyState icon={BellOff} title={emptyHint} />
      </Card>
    );
  }

  return (
    <Card divided>
      {alerts.map((alert) => (
        <AlertRow key={alert.id} alert={alert} busy={busyId === alert.id} onStatus={onStatus} />
      ))}
    </Card>
  );
};
